/**
 * Remesh & Decimate panel logic (pure): the panel's parameter state, the
 * document ops it builds (`remesh` / `decimate`, see prism_model schema.md),
 * the destructive `modelOp` commands, the `remeshPreview` request, the
 * engine's `remeshStats` event, and the Shift+R voxel-size drag mapping.
 *
 * Built ops omit parameters equal to the format defaults and parameters the
 * chosen mode does not read, but always carry `mode`.
 */

import type { ModelOp } from '../../lib/model-doc.js';

export type RemeshTab = 'voxel' | 'quad' | 'decimate';
export type RemeshMode = 'voxel' | 'quad';
export type DecimateMode = 'collapse' | 'planar' | 'unsubdivide';
export type SymmetryAxis = 'x' | 'y' | 'z';
export type DelimitKind = 'normal' | 'material' | 'seam' | 'sharp' | 'uv';

export const DELIMIT_KINDS: readonly DelimitKind[] = ['normal', 'material', 'seam', 'sharp', 'uv'];
export const DECIMATE_MODES: readonly DecimateMode[] = ['collapse', 'planar', 'unsubdivide'];

export interface VoxelParams {
  /** Meters; 0 = auto (longest side / 64). */
  voxelSize: number;
  adaptivity: number;
  fixPoles: boolean;
  smoothNormals: boolean;
  preserveVolume: boolean;
  projectColors: boolean;
  projectUvs: boolean;
}

export interface QuadParams {
  targetFaces: number;
  adaptiveSize: number;
  symmetry: SymmetryAxis | null;
  preserveSharp: boolean;
  /** Degrees. */
  sharpAngle: number;
  preserveBoundary: boolean;
  /** Relax iterations. */
  smoothing: number;
}

export interface DecimateParams {
  // collapse
  ratio: number;
  /** Overrides `ratio` when > 0. */
  targetTriangles: number;
  preserveBoundary: boolean;
  preserveSeams: boolean;
  preserveSharp: boolean;
  sharpAngle: number;
  symmetry: SymmetryAxis | null;
  triangulate: boolean;
  // planar
  angleLimit: number;
  delimit: DelimitKind[];
  allBoundaries: boolean;
  // unsubdivide
  iterations: number;
}

export const VOXEL_DEFAULTS: Readonly<VoxelParams> = {
  voxelSize: 0.05,
  adaptivity: 0,
  fixPoles: false,
  smoothNormals: false,
  preserveVolume: true,
  projectColors: false,
  projectUvs: false,
};

export const QUAD_DEFAULTS: Readonly<QuadParams> = {
  targetFaces: 2000,
  adaptiveSize: 0,
  symmetry: null,
  preserveSharp: false,
  sharpAngle: 40,
  preserveBoundary: true,
  smoothing: 3,
};

export const DECIMATE_DEFAULTS: Readonly<DecimateParams> = {
  ratio: 0.5,
  targetTriangles: 0,
  preserveBoundary: true,
  preserveSeams: true,
  preserveSharp: false,
  sharpAngle: 40,
  symmetry: null,
  triangulate: false,
  angleLimit: 5,
  delimit: ['normal'],
  allBoundaries: false,
  iterations: 1,
};

/** Keys each mode reads (everything else is left out of the op). */
const VOXEL_KEYS = Object.keys(VOXEL_DEFAULTS) as (keyof VoxelParams)[];
const QUAD_KEYS = Object.keys(QUAD_DEFAULTS) as (keyof QuadParams)[];
const DECIMATE_KEYS: Record<DecimateMode, (keyof DecimateParams)[]> = {
  collapse: ['ratio', 'targetTriangles', 'preserveBoundary', 'preserveSeams', 'preserveSharp', 'sharpAngle', 'symmetry', 'triangulate'],
  planar: ['angleLimit', 'delimit', 'allBoundaries'],
  unsubdivide: ['iterations'],
};

/* ------------------------------------------------------------------ */
/* Limits                                                              */

export const VOXEL_MIN = 0.001;
export const VOXEL_MAX = 10;
export const TARGET_FACES_MIN = 10;
export const TARGET_FACES_MAX = 1_000_000;
export const TARGET_TRIANGLES_MAX = 10_000_000;
export const SMOOTHING_MAX = 100;
export const UNSUBDIVIDE_MAX = 20;

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.min(max, Math.max(min, n));
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  return Math.round(clamp(value, min, max, fallback));
}

/** 0 (or less) = auto; otherwise kept in [VOXEL_MIN, VOXEL_MAX]. */
export function clampVoxelSize(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : VOXEL_DEFAULTS.voxelSize;
  return n <= 0 ? 0 : clamp(n, VOXEL_MIN, VOXEL_MAX, VOXEL_DEFAULTS.voxelSize);
}

function symmetryOf(value: unknown): SymmetryAxis | null {
  return value === 'x' || value === 'y' || value === 'z' ? value : null;
}

/** Valid, de-duplicated, in canonical order. */
export function cleanDelimit(value: unknown): DelimitKind[] {
  const list = Array.isArray(value) ? value : [];
  return DELIMIT_KINDS.filter((kind) => list.includes(kind));
}

export function clampVoxel(p: Partial<VoxelParams>): VoxelParams {
  const d = VOXEL_DEFAULTS;
  return {
    voxelSize: clampVoxelSize(p.voxelSize ?? d.voxelSize),
    adaptivity: clamp(p.adaptivity, 0, 1, d.adaptivity),
    fixPoles: p.fixPoles ?? d.fixPoles,
    smoothNormals: p.smoothNormals ?? d.smoothNormals,
    preserveVolume: p.preserveVolume ?? d.preserveVolume,
    projectColors: p.projectColors ?? d.projectColors,
    projectUvs: p.projectUvs ?? d.projectUvs,
  };
}

export function clampQuad(p: Partial<QuadParams>): QuadParams {
  const d = QUAD_DEFAULTS;
  return {
    targetFaces: clampInt(p.targetFaces, TARGET_FACES_MIN, TARGET_FACES_MAX, d.targetFaces),
    adaptiveSize: clamp(p.adaptiveSize, 0, 1, d.adaptiveSize),
    symmetry: symmetryOf(p.symmetry),
    preserveSharp: p.preserveSharp ?? d.preserveSharp,
    sharpAngle: clamp(p.sharpAngle, 0, 180, d.sharpAngle),
    preserveBoundary: p.preserveBoundary ?? d.preserveBoundary,
    smoothing: clampInt(p.smoothing, 0, SMOOTHING_MAX, d.smoothing),
  };
}

export function clampDecimate(p: Partial<DecimateParams>): DecimateParams {
  const d = DECIMATE_DEFAULTS;
  return {
    ratio: clamp(p.ratio, 0, 1, d.ratio),
    targetTriangles: clampInt(p.targetTriangles, 0, TARGET_TRIANGLES_MAX, d.targetTriangles),
    preserveBoundary: p.preserveBoundary ?? d.preserveBoundary,
    preserveSeams: p.preserveSeams ?? d.preserveSeams,
    preserveSharp: p.preserveSharp ?? d.preserveSharp,
    sharpAngle: clamp(p.sharpAngle, 0, 180, d.sharpAngle),
    symmetry: symmetryOf(p.symmetry),
    triangulate: p.triangulate ?? d.triangulate,
    angleLimit: clamp(p.angleLimit, 0, 180, d.angleLimit),
    delimit: p.delimit === undefined ? d.delimit.slice() : cleanDelimit(p.delimit),
    allBoundaries: p.allBoundaries ?? d.allBoundaries,
    iterations: clampInt(p.iterations, 1, UNSUBDIVIDE_MAX, d.iterations),
  };
}

/* ------------------------------------------------------------------ */
/* Panel state                                                         */

export interface RemeshPanelState {
  tab: RemeshTab;
  decimateMode: DecimateMode;
  voxel: VoxelParams;
  quad: QuadParams;
  decimate: DecimateParams;
}

export const DEFAULT_REMESH_STATE: RemeshPanelState = {
  tab: 'voxel',
  decimateMode: 'collapse',
  voxel: { ...VOXEL_DEFAULTS },
  quad: { ...QUAD_DEFAULTS },
  decimate: { ...DECIMATE_DEFAULTS, delimit: DECIMATE_DEFAULTS.delimit.slice() },
};

export type RemeshAction =
  | { type: 'tab'; tab: RemeshTab }
  | { type: 'decimateMode'; mode: DecimateMode }
  | { type: 'voxel'; patch: Partial<VoxelParams> }
  | { type: 'quad'; patch: Partial<QuadParams> }
  | { type: 'decimate'; patch: Partial<DecimateParams> }
  | { type: 'reset' };

/** Panel state transitions; every parameter patch is clamped. */
export function remeshReducer(state: RemeshPanelState, action: RemeshAction): RemeshPanelState {
  switch (action.type) {
    case 'tab':
      return action.tab === state.tab ? state : { ...state, tab: action.tab };
    case 'decimateMode':
      return DECIMATE_MODES.includes(action.mode) ? { ...state, decimateMode: action.mode } : state;
    case 'voxel':
      return { ...state, voxel: clampVoxel({ ...state.voxel, ...action.patch }) };
    case 'quad':
      return { ...state, quad: clampQuad({ ...state.quad, ...action.patch }) };
    case 'decimate':
      return { ...state, decimate: clampDecimate({ ...state.decimate, ...action.patch }) };
    case 'reset':
      return { ...DEFAULT_REMESH_STATE, tab: state.tab, decimateMode: state.decimateMode };
    default:
      return state;
  }
}

/* ------------------------------------------------------------------ */
/* Ops and commands                                                    */

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** `keys` of `params` that differ from `defaults` (null symmetry is never written). */
function changed<T extends object>(params: T, defaults: T, keys: readonly (keyof T)[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    const value = params[key];
    if (value === null || value === undefined) continue;
    if (!same(value, defaults[key])) out[key as string] = value;
  }
  return out;
}

/** Remesh mode used by the panel: the quad tab remeshes to quads, everything else is voxel. */
export function remeshModeOf(state: RemeshPanelState): RemeshMode {
  return state.tab === 'quad' ? 'quad' : 'voxel';
}

/** The `remesh` document op for the given mode (default: the panel's tab). */
export function buildRemeshOp(state: RemeshPanelState, mode: RemeshMode = remeshModeOf(state)): ModelOp {
  const params =
    mode === 'quad'
      ? changed(clampQuad(state.quad), QUAD_DEFAULTS, QUAD_KEYS)
      : changed(clampVoxel(state.voxel), VOXEL_DEFAULTS, VOXEL_KEYS);
  return { op: 'remesh', mode, ...params };
}

/** The `decimate` document op for the given method (default: the panel's sub-mode). */
export function buildDecimateOp(state: RemeshPanelState, mode: DecimateMode = state.decimateMode): ModelOp {
  const params = changed(clampDecimate(state.decimate), DECIMATE_DEFAULTS, DECIMATE_KEYS[mode]);
  return { op: 'decimate', mode, ...params };
}

/** The op the panel's current tab describes. */
export function buildOp(state: RemeshPanelState): ModelOp {
  return state.tab === 'decimate' ? buildDecimateOp(state) : buildRemeshOp(state);
}

export type RemeshKind = 'remesh' | 'decimate';

export function kindOf(state: RemeshPanelState): RemeshKind {
  return state.tab === 'decimate' ? 'decimate' : 'remesh';
}

function withoutOp(op: ModelOp): Record<string, unknown> {
  const { op: _name, ...params } = op;
  return params;
}

/** Destructive engine command: bake the part's shape into the remeshed / decimated mesh. */
export function buildModelOp(state: RemeshPanelState, path: string, op: ModelOp = buildOp(state)): Record<string, unknown> {
  return { op: 'modelOp', name: op.op, path, ...withoutOp(op) };
}

/** `remeshPreview` command: the engine answers with a `remeshStats` event. */
export function previewParams(state: RemeshPanelState, path: string, op: ModelOp = buildOp(state)): Record<string, unknown> {
  return { op: 'modelOp', name: 'remeshPreview', path, kind: op.op as RemeshKind, ...withoutOp(op) };
}

/* ------------------------------------------------------------------ */
/* Stats (`remeshStats` event)                                         */

export interface MeshCounts {
  vertices: number;
  faces: number;
  triangles: number;
  quads: number;
}

export interface RemeshStats {
  path: string;
  kind: RemeshKind;
  before: MeshCounts;
  after: MeshCounts;
  /** True when the engine only estimated `after` (job too heavy to run live). */
  estimated: boolean;
  ms: number;
}

function counts(value: unknown): MeshCounts | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const n = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : 0);
  return { vertices: n(v.vertices), faces: n(v.faces), triangles: n(v.triangles), quads: n(v.quads) };
}

/** Validate an engine `remeshStats` event payload. */
export function parseRemeshStats(event: unknown): RemeshStats | null {
  if (!event || typeof event !== 'object') return null;
  const e = event as Record<string, unknown>;
  if (typeof e.path !== 'string' || (e.kind !== 'remesh' && e.kind !== 'decimate')) return null;
  const before = counts(e.before);
  const after = counts(e.after);
  if (!before || !after) return null;
  return { path: e.path, kind: e.kind, before, after, estimated: e.estimated === true, ms: typeof e.ms === 'number' ? e.ms : 0 };
}

type StatsListener = (stats: RemeshStats) => void;
const statsListeners = new Set<StatsListener>();

/** Subscribe to `remeshStats` events; returns the unsubscribe function. */
export function onRemeshStats(listener: StatsListener): () => void {
  statsListeners.add(listener);
  return () => {
    statsListeners.delete(listener);
  };
}

/** Called by the engine event router for `remeshStats`. */
export function remeshStatsFromEngine(event: unknown): void {
  const stats = parseRemeshStats(event);
  if (!stats) return;
  for (const listener of [...statsListeners]) listener(stats);
}

export function formatCount(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** Share of quads among the result's faces, 0..100. */
export function quadPercent(c: MeshCounts): number {
  return c.faces > 0 ? Math.round((c.quads / c.faces) * 100) : 0;
}

/**
 * "Triangles: 12,480 → 2,016" (+ " · 96% quads" for quad remesh); the result
 * reads "…" while a preview is pending, the current count "…" until known,
 * and gets a "~" when the engine only estimated it.
 */
export function statsLine(stats: RemeshStats | null, pending: boolean, quads = false): string {
  const current = stats ? formatCount(stats.before.triangles) : '…';
  const result = !stats || pending ? '…' : `${stats.estimated ? '~' : ''}${formatCount(stats.after.triangles)}`;
  const tail = quads && stats && !pending ? ` · ${quadPercent(stats.after)}% quads` : '';
  return `Triangles: ${current} → ${result}${tail}`;
}

/* ------------------------------------------------------------------ */
/* Shift+R voxel-size gesture                                          */

/** Drag distance (CSS px) that doubles / halves the voxel size. */
export const PX_PER_DOUBLING = 120;
/** Starting size when the panel's size is auto (0). */
export const GESTURE_FALLBACK_SIZE = VOXEL_DEFAULTS.voxelSize;

/** Round to 3 significant digits (readable sizes while dragging). */
export function roundSignificant(value: number, digits = 3): number {
  if (!Number.isFinite(value) || value === 0) return 0;
  const scale = 10 ** (digits - 1 - Math.floor(Math.log10(Math.abs(value))));
  return Math.round(value * scale) / scale;
}

/**
 * Voxel size after dragging `dx` px horizontally from a gesture that started
 * at `start` meters: log scale (right = bigger), clamped to the valid range.
 */
export function voxelSizeFromDrag(start: number, dx: number, pxPerDoubling = PX_PER_DOUBLING): number {
  const base = Number.isFinite(start) && start > 0 ? start : GESTURE_FALLBACK_SIZE;
  const steps = Number.isFinite(dx) ? dx / Math.max(1, pxPerDoubling) : 0;
  const size = base * 2 ** steps;
  return roundSignificant(Math.min(VOXEL_MAX, Math.max(VOXEL_MIN, size)));
}

/** "12 mm", "4.5 mm", "0.25 m"... */
export function formatVoxelSize(meters: number): string {
  if (meters <= 0) return 'auto';
  if (meters < 0.01) return `${roundSignificant(meters * 1000, 2)} mm`;
  if (meters < 1) return `${roundSignificant(meters * 1000, 3)} mm`;
  return `${roundSignificant(meters, 3)} m`;
}
